package com.gloopgo.game.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.ui.Alignment
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.gloopgo.game.audio.SoundManager
import com.gloopgo.game.legal.PrivacyPolicy
import com.gloopgo.game.audio.getSoundManager
import com.gloopgo.game.moderation.BlockedPlayersStore
import com.gloopgo.game.network.MultiplayerRepository
import gloopgo.shared.generated.resources.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

@Composable
fun SettingsScreen(
    useJoystick: Boolean = false,
    onToggleJoystick: (Boolean) -> Unit = {},
    soundManager: SoundManager = getSoundManager(),
    repository: MultiplayerRepository? = null,
    onUserDataCleared: () -> Unit = {},
    onBackToMenu: () -> Unit
) {
    val uriHandler = LocalUriHandler.current
    var isSoundEnabled by remember { mutableStateOf(soundManager.isSoundEnabled) }
    val platformInfo = remember { getAppPlatformInfo() }
    val coroutineScope = rememberCoroutineScope()
    val blockedPlayersStore: BlockedPlayersStore = koinInject()
    var showClearDialog by remember { mutableStateOf(false) }
    var isClearingData by remember { mutableStateOf(false) }
    var clearDataSuccess by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(
                        Color(0xFF0F172A),
                        Color(0xFF090D16)
                    )
                )
            )
    ) {
        Scaffold(
            containerColor = Color.Transparent,
            contentWindowInsets = WindowInsets.safeDrawing,
            topBar = {
                NeonAbyssTopAppBar(
                    title = stringResource(Res.string.settings_title),
                    onBack = onBackToMenu,
                    enabled = !isClearingData,
                )
            },
        ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding),
            contentAlignment = Alignment.Center
        ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            contentAlignment = Alignment.Center
        ) {
            val isExpanded = maxWidth > 720.dp
            val isCompactHeight = maxHeight < 560.dp

            if (isExpanded) {
                // Wide / Tablet / Desktop: Two-Pane Adaptive Layout
                Column(
                    modifier = Modifier
                        .widthIn(max = 880.dp)
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState()),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Header
                    Text(
                        text = stringResource(Res.string.settings_title),
                        fontSize = 26.sp,
                        fontWeight = FontWeight.Black,
                        color = MaterialTheme.colorScheme.onSurface,
                        letterSpacing = 1.sp
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = stringResource(Res.string.settings_subtitle),
                        fontSize = 12.sp,
                        color = Color(0xFF94A3B8)
                    )

                    Spacer(modifier = Modifier.height(20.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(20.dp),
                        verticalAlignment = Alignment.Top
                    ) {
                        // Left Pane: Controls & Input Preferences
                        Card(
                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                            shape = RoundedCornerShape(24.dp),
                            modifier = Modifier.weight(1f),
                            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                        ) {
                            Column(
                                modifier = Modifier.padding(if (isCompactHeight) 18.dp else 24.dp)
                            ) {
                                Text(
                                    text = stringResource(Res.string.settings_preferences),
                                    fontSize = 16.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFF00E5FF)
                                )

                                Spacer(modifier = Modifier.height(16.dp))

                                JoystickSettingRow(
                                    useJoystick = useJoystick,
                                    onToggleJoystick = onToggleJoystick
                                )

                                Spacer(modifier = Modifier.height(12.dp))

                                SoundSettingRow(
                                    soundEnabled = isSoundEnabled,
                                    onToggleSound = {
                                        soundManager.isSoundEnabled = it
                                        isSoundEnabled = it
                                        if (it) soundManager.playPelletEatenSound()
                                    }
                                )

                                Spacer(modifier = Modifier.height(16.dp))

                                BlockedPlayersSection(
                                    store = blockedPlayersStore,
                                    repository = repository
                                )

                                Spacer(modifier = Modifier.height(14.dp))

                                // Informational status card
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .background(Color(0xFF0F172A), RoundedCornerShape(12.dp))
                                        .padding(14.dp)
                                ) {
                                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                                        Text(
                                            text = if (useJoystick) stringResource(Res.string.settings_joystick_mode_active) else stringResource(Res.string.settings_pointer_mode_active),
                                            fontWeight = FontWeight.Bold,
                                            fontSize = 13.sp,
                                            color = if (useJoystick) Color(0xFF00E5FF) else Color(0xFFFFD600)
                                        )
                                        Text(
                                            text = if (useJoystick) {
                                                stringResource(Res.string.settings_joystick_mode_desc)
                                            } else {
                                                stringResource(Res.string.settings_pointer_mode_desc)
                                            },
                                            fontSize = 11.sp,
                                            color = Color(0xFF94A3B8),
                                            lineHeight = 16.sp
                                        )
                                    }
                                }
                            }
                        }

                        // Right Pane: Keybindings & Controls Reference Guide
                        Card(
                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                            shape = RoundedCornerShape(24.dp),
                            modifier = Modifier.weight(1f),
                            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                        ) {
                            Column(
                                modifier = Modifier.padding(if (isCompactHeight) 18.dp else 24.dp)
                            ) {
                                Text(
                                    text = stringResource(Res.string.settings_controls_title),
                                    fontSize = 16.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFFFFD600)
                                )

                                Spacer(modifier = Modifier.height(16.dp))

                                ControlsList(useJoystick = useJoystick)
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(20.dp))

                    Card(
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                        shape = RoundedCornerShape(24.dp),
                        modifier = Modifier.fillMaxWidth(),
                        elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                    ) {
                        Column(modifier = Modifier.padding(if (isCompactHeight) 18.dp else 24.dp)) {
                            Text(
                                text = stringResource(Res.string.privacy_card_title),
                                fontSize = 16.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFFFF5252)
                            )
                            Spacer(modifier = Modifier.height(10.dp))
                            Text(
                                text = stringResource(Res.string.privacy_card_desc),
                                fontSize = 12.sp,
                                color = Color(0xFF94A3B8),
                                lineHeight = 17.sp
                            )
                            Spacer(modifier = Modifier.height(12.dp))
                            Button(
                                onClick = { uriHandler.openUri(PrivacyPolicy.URL) },
                                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF334155)),
                                shape = RoundedCornerShape(10.dp),
                                modifier = Modifier.fillMaxWidth().height(42.dp)
                            ) {
                                Text(stringResource(Res.string.privacy_open_button), fontWeight = FontWeight.Bold, color = Color.White)
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(20.dp))

                    ClearDataCard(
                        isClearingData = isClearingData,
                        clearDataSuccess = clearDataSuccess,
                        onRequestClear = {
                            clearDataSuccess = false
                            showClearDialog = true
                        }
                    )

                    Spacer(modifier = Modifier.height(14.dp))

                    Text(
                        text = stringResource(Res.string.settings_version, platformInfo.appVersion),
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium,
                        color = Color(0xFF64748B)
                    )
                }
            } else {
                // Compact screen (Mobile / Portrait / Narrow)
                Card(
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                    shape = RoundedCornerShape(24.dp),
                    modifier = Modifier
                        .widthIn(max = 440.dp)
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState()),
                    elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(if (isCompactHeight) 18.dp else 24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = stringResource(Res.string.settings_title),
                            fontSize = 22.sp,
                            fontWeight = FontWeight.Black,
                            color = MaterialTheme.colorScheme.onSurface,
                            letterSpacing = 1.sp
                        )

                        Spacer(modifier = Modifier.height(18.dp))

                        // Use Joystick Option
                        JoystickSettingRow(
                            useJoystick = useJoystick,
                            onToggleJoystick = onToggleJoystick
                        )

                        Spacer(modifier = Modifier.height(12.dp))

                        // Sound Effects Option
                        SoundSettingRow(
                            soundEnabled = isSoundEnabled,
                            onToggleSound = {
                                soundManager.isSoundEnabled = it
                                isSoundEnabled = it
                                if (it) soundManager.playPelletEatenSound()
                            }
                        )

                        Spacer(modifier = Modifier.height(18.dp))

                        BlockedPlayersSection(
                            store = blockedPlayersStore,
                            repository = repository
                        )

                        Spacer(modifier = Modifier.height(18.dp))

                        // Controls summary
                        Text(
                            text = stringResource(Res.string.settings_controls_title_compact),
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF94A3B8),
                            modifier = Modifier.fillMaxWidth()
                        )
                        Spacer(modifier = Modifier.height(8.dp))

                        ControlsList(useJoystick = useJoystick)

                        Spacer(modifier = Modifier.height(18.dp))

                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFF0F172A), RoundedCornerShape(12.dp))
                                .padding(14.dp)
                        ) {
                            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text(
                                    text = stringResource(Res.string.privacy_card_title),
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 13.sp,
                                    color = Color(0xFFFF5252)
                                )
                                Text(
                                    text = stringResource(Res.string.privacy_card_desc),
                                    fontSize = 11.sp,
                                    color = Color(0xFF94A3B8),
                                    lineHeight = 16.sp
                                )
                                Button(
                                    onClick = { uriHandler.openUri(PrivacyPolicy.URL) },
                                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF334155)),
                                    shape = RoundedCornerShape(10.dp),
                                    modifier = Modifier.fillMaxWidth().height(40.dp)
                                ) {
                                    Text(
                                        text = stringResource(Res.string.privacy_open_button),
                                        fontWeight = FontWeight.Bold,
                                        color = Color.White,
                                        fontSize = 12.sp
                                    )
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(18.dp))

                        ClearDataCard(
                            isClearingData = isClearingData,
                            clearDataSuccess = clearDataSuccess,
                            onRequestClear = {
                                clearDataSuccess = false
                                showClearDialog = true
                            },
                            compact = true
                        )

                        Spacer(modifier = Modifier.height(14.dp))

                        Text(
                            text = stringResource(Res.string.settings_version, platformInfo.appVersion),
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Medium,
                            color = Color(0xFF64748B)
                        )
                    }
                }
            }
        }

        if (showClearDialog) {
            AlertDialog(
                onDismissRequest = { if (!isClearingData) showClearDialog = false },
                title = {
                    Text(
                        text = "⚠ ${stringResource(Res.string.clear_data_dialog_title)}",
                        fontWeight = FontWeight.Black,
                        color = Color(0xFFFF5252),
                        fontSize = 16.sp
                    )
                },
                text = {
                    Text(
                        text = stringResource(Res.string.clear_data_dialog_desc),
                        fontSize = 13.sp,
                        color = Color(0xFFCBD5E1)
                    )
                },
                confirmButton = {
                    Button(
                        onClick = {
                            coroutineScope.launch {
                                isClearingData = true
                                try {
                                    val uid = repository?.getCurrentUserId().orEmpty()
                                    val result = repository?.clearUserData(uid)
                                        ?: Result.failure(IllegalStateException("Repository unavailable"))
                                    showClearDialog = false
                                    isClearingData = false
                                    if (result.isSuccess) {
                                        blockedPlayersStore.clear()
                                        clearDataSuccess = true
                                        onUserDataCleared()
                                    }
                                } catch (_: Throwable) {
                                    showClearDialog = false
                                    isClearingData = false
                                }
                            }
                        },
                        enabled = !isClearingData,
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFF5252))
                    ) {
                        if (isClearingData) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(16.dp),
                                color = Color.White,
                                strokeWidth = 2.dp
                            )
                        } else {
                            Text(
                                text = stringResource(Res.string.clear_data_confirm),
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )
                        }
                    }
                },
                dismissButton = {
                    TextButton(
                        onClick = { showClearDialog = false },
                        enabled = !isClearingData
                    ) {
                        Text(
                            text = stringResource(Res.string.cancel),
                            color = Color(0xFF94A3B8)
                        )
                    }
                },
                containerColor = Color(0xFF1E293B)
            )
        }
        }
        }
    }
}

@Composable
private fun ClearDataCard(
    isClearingData: Boolean,
    clearDataSuccess: Boolean,
    onRequestClear: () -> Unit,
    compact: Boolean = false,
) {
    if (compact) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(Color(0xFF0F172A), RoundedCornerShape(12.dp))
                .padding(14.dp)
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    text = stringResource(Res.string.clear_data_card_title),
                    fontWeight = FontWeight.Bold,
                    fontSize = 13.sp,
                    color = Color(0xFFFF5252)
                )
                Text(
                    text = stringResource(Res.string.clear_data_desc),
                    fontSize = 11.sp,
                    color = Color(0xFF94A3B8),
                    lineHeight = 16.sp
                )
                Button(
                    onClick = onRequestClear,
                    enabled = !isClearingData,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFFFF5252),
                        disabledContainerColor = Color(0xFFFF5252).copy(alpha = 0.4f)
                    ),
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth().height(40.dp)
                ) {
                    if (isClearingData) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            color = Color.White,
                            strokeWidth = 2.dp
                        )
                    } else {
                        Text(
                            text = "⚠ ${stringResource(Res.string.clear_data_button)}",
                            fontWeight = FontWeight.Bold,
                            color = Color.White,
                            fontSize = 12.sp
                        )
                    }
                }
                if (clearDataSuccess) {
                    Text(
                        text = "✔ ${stringResource(Res.string.clear_data_success)}",
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF00E5FF)
                    )
                }
            }
        }
    } else {
        Card(
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
            shape = RoundedCornerShape(24.dp),
            modifier = Modifier.fillMaxWidth(),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
        ) {
            Column(modifier = Modifier.padding(24.dp)) {
                Text(
                    text = stringResource(Res.string.clear_data_card_title),
                    fontSize = 16.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFFFF5252)
                )
                Spacer(modifier = Modifier.height(10.dp))
                Text(
                    text = stringResource(Res.string.clear_data_desc),
                    fontSize = 12.sp,
                    color = Color(0xFF94A3B8),
                    lineHeight = 17.sp
                )
                Spacer(modifier = Modifier.height(12.dp))
                Button(
                    onClick = onRequestClear,
                    enabled = !isClearingData,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFFFF5252),
                        disabledContainerColor = Color(0xFFFF5252).copy(alpha = 0.4f)
                    ),
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth().height(42.dp)
                ) {
                    if (isClearingData) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            color = Color.White,
                            strokeWidth = 2.dp
                        )
                    } else {
                        Text(
                            text = "⚠ ${stringResource(Res.string.clear_data_button)}",
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                    }
                }
                if (clearDataSuccess) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = "✔ ${stringResource(Res.string.clear_data_success)}",
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF00E5FF)
                    )
                }
            }
        }
    }
}

@Composable
private fun JoystickSettingRow(
    useJoystick: Boolean,
    onToggleJoystick: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFF0F172A), RoundedCornerShape(12.dp))
            .padding(horizontal = 16.dp, vertical = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = stringResource(Res.string.settings_use_joystick),
                fontSize = 14.sp,
                fontWeight = FontWeight.Bold,
                color = Color.White
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = stringResource(Res.string.settings_use_joystick_desc),
                fontSize = 11.sp,
                color = Color(0xFF94A3B8),
                lineHeight = 15.sp
            )
        }

        Spacer(modifier = Modifier.width(8.dp))

        Switch(
            checked = useJoystick,
            onCheckedChange = onToggleJoystick,
            colors = SwitchDefaults.colors(
                checkedThumbColor = Color.Black,
                checkedTrackColor = Color(0xFF00E5FF),
                uncheckedThumbColor = Color(0xFF94A3B8),
                uncheckedTrackColor = Color(0xFF1E293B)
            )
        )
    }
}

@Composable
private fun ControlsList(useJoystick: Boolean) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFF0F172A), RoundedCornerShape(12.dp))
            .padding(14.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (useJoystick) {
                Text(stringResource(Res.string.settings_ctrl_steer_joystick), fontSize = 12.sp, color = Color.White)
            } else {
                Text(stringResource(Res.string.settings_ctrl_steer_pointer), fontSize = 12.sp, color = Color.White)
            }
            Text(stringResource(Res.string.settings_ctrl_split), fontSize = 12.sp, color = Color.White)
            Text(stringResource(Res.string.settings_ctrl_eject), fontSize = 12.sp, color = Color.White)
            Text(stringResource(Res.string.settings_ctrl_pause), fontSize = 12.sp, color = Color.White)
            Text(stringResource(Res.string.settings_ctrl_toggle_joystick), fontSize = 12.sp, color = Color.White)
            Text(stringResource(Res.string.settings_ctrl_zoom), fontSize = 12.sp, color = Color.White)
            Text(stringResource(Res.string.settings_ctrl_leave), fontSize = 12.sp, color = Color(0xFF94A3B8))
        }
    }
}

@Composable
private fun SoundSettingRow(
    soundEnabled: Boolean,
    onToggleSound: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFF0F172A), RoundedCornerShape(12.dp))
            .padding(horizontal = 16.dp, vertical = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = stringResource(Res.string.settings_sound_effects),
                fontSize = 14.sp,
                fontWeight = FontWeight.Bold,
                color = Color.White
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = stringResource(Res.string.settings_sound_effects_desc),
                fontSize = 11.sp,
                color = Color(0xFF94A3B8),
                lineHeight = 15.sp
            )
        }

        Spacer(modifier = Modifier.width(8.dp))

        Switch(
            checked = soundEnabled,
            onCheckedChange = onToggleSound,
            colors = SwitchDefaults.colors(
                checkedThumbColor = Color.Black,
                checkedTrackColor = Color(0xFF00E5FF),
                uncheckedThumbColor = Color(0xFF94A3B8),
                uncheckedTrackColor = Color(0xFF1E293B)
            )
        )
    }
}

@Composable
private fun BlockedPlayersSection(
    store: BlockedPlayersStore,
    repository: MultiplayerRepository?
) {
    val blocked by store.blocked.collectAsStateWithLifecycle()
    val scope = rememberCoroutineScope()
    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            text = stringResource(Res.string.blocked_players_title),
            fontSize = 13.sp,
            fontWeight = FontWeight.Bold,
            color = Color(0xFFFFD600)
        )
        if (blocked.isEmpty()) {
            Text(
                text = stringResource(Res.string.blocked_players_empty),
                fontSize = 12.sp,
                color = Color(0xFF94A3B8)
            )
        } else {
            blocked.forEach { player ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(Color(0xFF0F172A), RoundedCornerShape(10.dp))
                        .padding(horizontal = 12.dp, vertical = 6.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = player.nickname.ifBlank { stringResource(Res.string.blocked_player_name) },
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Color.White,
                        modifier = Modifier.weight(1f)
                    )
                    TextButton(
                        onClick = {
                            store.unblock(player.uid)
                            scope.launch {
                                val ownerUid = repository?.getCurrentUserId().orEmpty()
                                if (ownerUid.isNotEmpty()) {
                                    repository?.setPlayerBlocked(ownerUid, player.uid, player.nickname, blocked = false)
                                }
                            }
                        }
                    ) {
                        Text(
                            text = stringResource(Res.string.blocked_players_unblock),
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF00E5FF)
                        )
                    }
                }
            }
        }
    }
}



